/** Budget per account — API-laag (/Api/budget). */
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BACKEND_URL}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    fout.huidig = d.huidig;
    throw fout;
  }
  return d;
}

const q = (jaar) => (jaar ? `?jaar=${encodeURIComponent(jaar)}` : '');

/** Bestandsnaam uit Content-Disposition: filename* (RFC 5987) wint van filename. */
export function naamUitHeader(header, standaard) {
  const h = String(header || '');
  const ster = h.match(/filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/);
  if (ster) {
    try { return decodeURIComponent(ster[1].trim().replace(/^"|"$/g, '')); } catch { /* val terug */ }
  }
  const gewoon = h.match(/filename\s*=\s*"([^"]+)"/) || h.match(/filename\s*=\s*([^;]+)/);
  return gewoon ? gewoon[1].trim() : standaard;
}

export const budgetApi = {
  overzicht: (jaar) => vraag(`/Api/budget/overzicht${q(jaar)}`),
  detail: (sleutel, jaar) => vraag(`/Api/budget/account/${encodeURIComponent(sleutel)}${q(jaar)}`),
  bewaar: (sleutel, body, jaar) => vraag(`/Api/budget/account/${encodeURIComponent(sleutel)}${q(jaar)}`, { method: 'PUT', body }),
  gewichten: (body, jaar) => vraag(`/Api/budget/gewichten${q(jaar)}`, { method: 'PUT', body }),
  async importTargets(bestand, { jaar, toepassen = false, overschrijf = false, metGelijkend = true, voorstelId } = {}) {
    const p = new URLSearchParams({ toepassen: String(toepassen), overschrijf: String(overschrijf), met_gelijkend: String(metGelijkend) });
    if (jaar) p.set('jaar', String(jaar));
    if (voorstelId) p.set('voorstel_id', voorstelId);
    const r = await authFetch(`${BACKEND_URL}/Api/budget/import-targets-vorig?${p}`, {
      method: 'POST', body: bestand, headers: { 'Content-Type': 'application/octet-stream' },
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const fout = new Error(typeof d.detail === 'string' ? d.detail : `Importeren mislukt (${r.status})`);
      fout.status = r.status;
      fout.voorstel = d.voorstel;
      throw fout;
    }
    return d;
  },
  // Bedrijf zonder account koppelen (schrijft Airtable Accounts.Bedrijven) en ongedaan maken.
  koppel: (companyId, accountSleutel, jaar) => vraag(`/Api/budget/koppel${q(jaar)}`, {
    method: 'POST', body: { company_id: companyId, account_sleutel: accountSleutel },
  }),
  // Undo van precies één koppel-actie: de engine weigert (409) als de koppeling intussen veranderde.
  ontkoppel: ({ companyId, accountSleutel, bedrijfRecordId, wijzigingId }, jaar) => vraag(`/Api/budget/ontkoppel${q(jaar)}`, {
    method: 'POST',
    body: { company_id: companyId, account_sleutel: accountSleutel, bedrijf_record_id: bedrijfRecordId, wijziging_id: wijzigingId },
  }),
  // Nieuwe klant (handmatige regel): alleen budget, niets naar Airtable/Gripp. Verwijderen kan alleen
  // zonder koppeling en wordt door het scherm bevestigd + ongedaan te maken (herstelNieuweKlant).
  nieuweKlant: ({ naam, portfolio }, jaar) => vraag(`/Api/budget/nieuwe-klant${q(jaar)}`, {
    method: 'POST', body: { naam, portfolio: portfolio || null },
  }),
  verwijderNieuweKlant: (sleutel, versie, jaar) => {
    const p = new URLSearchParams({ versie: String(versie) });
    if (jaar) p.set('jaar', String(jaar));
    return vraag(`/Api/budget/nieuwe-klant/${encodeURIComponent(sleutel)}?${p}`, { method: 'DELETE' });
  },
  herstelNieuweKlant: (herstel, jaar) => vraag(`/Api/budget/nieuwe-klant/herstel${q(jaar)}`, { method: 'POST', body: herstel }),
  koppelNieuweKlant: (sleutel, gekoppeldAan, versie, jaar) => vraag(
    `/Api/budget/nieuwe-klant/${encodeURIComponent(sleutel)}/koppeling${q(jaar)}`,
    { method: 'PUT', body: { gekoppeld_aan: gekoppeldAan, versie } },
  ),
  herstelImport: (items, jaar) => vraag(`/Api/budget/import-targets-vorig/herstel${q(jaar)}`, { method: 'POST', body: { items } }),
  // Groeirapport als PDF; de engine bepaalt de bestandsnaam (datum van vandaag).
  async rapport(jaar) {
    const r = await authFetch(`${BACKEND_URL}/Api/budget/rapport.pdf${q(jaar)}`);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      const reden = typeof d.detail === 'string' ? d.detail : (typeof d.error === 'string' ? d.error : '');
      throw new Error(reden ? `Rapport maken mislukt: ${reden}` : `Rapport maken mislukt (${r.status})`);
    }
    const naam = naamUitHeader(r.headers.get('content-disposition'), `Budget ${jaar || ''} rapport.pdf`.replace('  ', ' '));
    return { blob: await r.blob(), naam };
  },
  async export(jaar) {
    const r = await authFetch(`${BACKEND_URL}/Api/budget/export.xlsx${q(jaar)}`);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(typeof d.detail === 'string' ? d.detail : `Exporteren mislukt (${r.status})`);
    }
    return r.blob();
  },
};
